import React from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import VisibilityIcon from '@material-ui/icons/Visibility';
import RemoveIcon from '@material-ui/icons/Remove';
import { Box, Grid, TableBody, TableCell, TableRow, Tooltip, Typography } from "@material-ui/core";
import { POLYGON_CONSTANT_TEXT, TABLE_BODY_NO_KML_FILE_ISSUE, TABLE_BODY_UPLOAD_KML_FILE_ISSUE } from "../Utils/PlolygonServicesConstants";
import { allowPolygonServiceEdit } from "../Utils/PlolygonServicesUtils";
import { GetNoDataPresentAfterFiltereApplied, GetNoDataPresentAfterSearchApplied, GetNoDataPresentErrorViewUtils } from "../../common/App/AppViewUtils";
import { APP_ROUTES } from "../../../config/constants";
import { isArrayValid, isEmptyOrNull, isObjectEmptyOrNull, isUndefined } from "../../../utils/DataUtils";

interface PlolygonServicesCardBodyInterface {
  classes: any;
  rowsData: any;
  currentCountry: any;
  userInfo: any;
  appFilterPopUpObj: any;
  appStateObj: any;
  appliedServiceTypes: Array<string>;
  totalColumnCount: number;
  handlePolygonDetailsClick: (polygonId: string, serviceType?: string) => void;
}

const PlolygonServicesCardBody = (props: PlolygonServicesCardBodyInterface) => {
  const { classes, rowsData, currentCountry, userInfo, appFilterPopUpObj, appStateObj, appliedServiceTypes, totalColumnCount, handlePolygonDetailsClick } = props;

  const TimeRangeDisplay: React.FC<{ timeRange: string; configText: string; className: string; tooltipContent?: React.ReactNode; }> = ({ timeRange, configText, className, tooltipContent }) => {
    const content = (
      <Typography className={className}>
        {`${timeRange} - ${configText}`}
      </Typography>
    );

    return tooltipContent ? (
      <Tooltip title={tooltipContent}>
        {content}
      </Tooltip>
    ) : (
      content
    );
  };

  const PosZoneMappingList = ({ posZoneMappings }: { posZoneMappings: any[] }) => {
    if (!isArrayValid(posZoneMappings)) {
      return null;
    }

    return (
      <Box>
        {posZoneMappings.map((posZoneMappingObj: any, posZoneMappingIndex: number) => {
          let configText: string = posZoneMappingObj?.address ?? "";
          return (
            <Typography
              key={`posZoneMapping-${posZoneMappingIndex}-${posZoneMappingObj?.posNo ?? "-"}`}
              className={classes.polygonServiceDetailsToolTipSpanStyle}
            >
              {configText ?? ""}
            </Typography>
          )
        })}
      </Box>
    );
  }

  const renderServiceConfiguration = (record: any, recordIndex: number) => {
    if (isUndefined(record?.serviceTypeMappings) || !isArrayValid(record.serviceTypeMappings)) {
      return null;
    }

    // Only render cells for the service types the currently-rendered data was fetched with (falls back to all before the first fetch completes)
    const visibleServiceMappings = record.serviceTypeMappings.filter(
      (serviceType: any) => !isArrayValid(appliedServiceTypes) || appliedServiceTypes.includes(serviceType?.name)
    );

    return (
      <>
        {visibleServiceMappings.map((serviceType: any, serviceTypeIndex: number) => {
          const mappings = serviceType?.mappings;
          const hasMultipleMappings = isArrayValid(mappings) && mappings.length > 1;

          return (
            <TableCell align="left" className={`${classes.cellDataStyle} ${classes.cellPolygonServiceStyle}`} key={`${serviceTypeIndex}-serviceTypeIndex-${recordIndex}`} onClick={() => handlePolygonDetailsClick(record?.polygonId, serviceType?.name)}>
              <Grid container spacing={0}>
                <Grid item xs={12}>
                  {hasMultipleMappings ? (
                    <Typography className={classes.polygonServiceMultipleAreaDetailsSpanStyle}>
                      {`Multiple (${mappings.length})`}
                    </Typography>
                  ) : !isUndefined(mappings) && isArrayValid(mappings) ? (
                    mappings.map((mappingObj: any, mappingObjIndex: number) => {
                      const startTime = moment(mappingObj?.startTime, POLYGON_CONSTANT_TEXT.BACK_END_TIME_FORMAT);
                      const endTime = moment(mappingObj?.endTime, POLYGON_CONSTANT_TEXT.BACK_END_TIME_FORMAT);

                      const formattedStartTime = startTime.isValid() ? startTime.format(POLYGON_CONSTANT_TEXT.VIEW_TIME_FORMAT) : "N/A";
                      const formattedEndTime = endTime.isValid() ? endTime.format(POLYGON_CONSTANT_TEXT.VIEW_TIME_FORMAT) : "N/A";

                      const timeRange = `${formattedStartTime} : ${formattedEndTime}`;
                      const isServiceable = mappingObj?.isServiceable ?? false;
                      const posZoneMappingArray = mappingObj?.posZoneMappingArray ?? [];

                      if (!isServiceable) {
                        return (
                          <div key={`mapping-${record?.polygonId}-${serviceTypeIndex}-${mappingObjIndex}`}>
                            <Typography className={classes.polygonNonServiceableSpanStyle}>
                              {`Non Serviceable Area`}
                            </Typography>
                          </div>
                        );
                      }

                      let configText: string = mappingObj?.address ?? "";
                      let className: string = classes.polygonServiceDetailsSpanStyle;
                      let tooltipContent: React.ReactNode | undefined;

                      if (isArrayValid(posZoneMappingArray)) {
                        const areaCount = posZoneMappingArray?.length ?? 0;
                        configText = `${areaCount} POS Configuration${areaCount !== 1 ? 's' : ''}`;
                        className = classes.polygonServiceMultipleAreaDetailsSpanStyle;
                        tooltipContent = (<PosZoneMappingList posZoneMappings={posZoneMappingArray} />)
                      }

                      return (
                        <div key={`mapping-${record.polygonId}-${serviceTypeIndex}-${mappingObjIndex}`}>
                          <TimeRangeDisplay
                            timeRange={timeRange}
                            configText={configText}
                            className={className}
                            tooltipContent={tooltipContent}
                          />
                        </div>
                      );
                    })
                  ) : (
                    <Typography className={classes.polygonServiceDetailsSpanStyle}>{`-`}</Typography>
                  )}
                </Grid>
              </Grid>
            </TableCell>
          );
        })}
      </>
    );
  };

  const renderNoData = () => {
    const { filterList } = appFilterPopUpObj ?? {};
    const { searchValue } = appStateObj ?? {};

    let NoDataComponent = null;

    if (!isObjectEmptyOrNull(filterList)) {
      NoDataComponent = <GetNoDataPresentAfterFiltereApplied />;
    } else if (!isEmptyOrNull(searchValue)) {
      NoDataComponent = <GetNoDataPresentAfterSearchApplied />;
    } else {
      NoDataComponent = (
        <GetNoDataPresentErrorViewUtils
          currentCountry={currentCountry}
          ErrorMessage={TABLE_BODY_NO_KML_FILE_ISSUE}
          SecondaryErrorMessage={TABLE_BODY_UPLOAD_KML_FILE_ISSUE}
        />
      );
    }

    return (
      <TableRow>
        <TableCell rowSpan={1} colSpan={totalColumnCount} className={classes.noDataRowStyle}>
          {NoDataComponent}
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableBody>
      {!isUndefined(rowsData) && isArrayValid(rowsData) ? (
        rowsData.map((record: any, recordIndex: number) => (
          <TableRow key={`${recordIndex}-record-rowsData-PlolygonServicesCardBody-${record?.polygonId}`}>
            <TableCell align="left" className={`${classes.cellDataStyle} ${classes.cellPolygonIdStyle}`}>
              <Grid container spacing={1} justifyContent="space-between">
                {!isEmptyOrNull(userInfo?.userProfile?.role) && allowPolygonServiceEdit(userInfo?.userProfile?.role) ?
                  (<Grid item className={classes.detailsCellStyle}>
                    <Link to={{ pathname: `${APP_ROUTES.POLYGON_SERVICES_CONFIGURATION}`, search: `?${POLYGON_CONSTANT_TEXT.POLYGON_ID_PARAM}=${record?.polygonId}` }} className={classes.cellDataHyperLinkStyle}>
                      <Typography className={classes.cellDataHyperLinkTextStyle}>{record?.polygonId ?? ""}</Typography>
                    </Link>
                  </Grid>)
                  : record?.polygonId ?? ""
                }
                <Grid item className={classes.detailsCellStyle}>
                  <VisibilityIcon className={classes.visibilityIconStyle} onClick={() => handlePolygonDetailsClick(record?.polygonId)} />
                </Grid>
              </Grid>
            </TableCell>
            {!isUndefined(record?.serviceTypeMappings) && isArrayValid(record.serviceTypeMappings) ? renderServiceConfiguration(record, recordIndex) : null}
          </TableRow>
        ))
      ) : (
        renderNoData()
      )}
    </TableBody>
  );
};

export default React.memo(PlolygonServicesCardBody);
